import * as React from 'react';
import debounce from 'lodash.debounce';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from "@mui/material/TextField";
import Skeleton from '@mui/material/Skeleton';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from '@mui/icons-material/Search';
import { Grid, InputAdornment } from '@mui/material';
import SearchListGrid from '../searchListGrid/SearchListGrid';
import {
    IOverrideRequestDetails,
    IOverrideRequestSearchParam
} from '../../../store/requestOverride/types';
import { HeadCell, Order } from '../tableGrid/types';
import EnhancedTable from '../tableGrid/tableGrid';
import EnhancedTableToolbar from '../tableGrid/tableToolbar';
import EnhancedTableHead from '../tableGrid/tableHead';
import Row from '../tableGrid/tableRow';
import { useNavigate } from 'react-router-dom';
import {
    patchOverrideRequest,
    fetchOverrideRequestsRequest
} from "../../../store/requestOverride/actions"
import { getOverrideRequestListPendingSelector, getOverrideRequestListSelector, patchOverrideRequestPendingSelector, patchOverrideRequestSelector } from '../../../store/requestOverride/selectors';
import { convertUTCDateToLocalDate, getRequestStatusText } from '../../shared/functions';
import { REQUEST_STATUS, REQUEST_STATUS_VALUES } from '../../../constants/_pickerValues';
import OverrideRequestUpdateStatus from '../../molecules/overrideRequestUpdateStatus/OverrideRequestUpdateStatus'
import { SEARCH_DEBOUNCE_TIME_MILLIS } from "../../../constants/common";
import {getProfileDataSelector} from "../../../store/auth/selectors";
import {OverridableStringUnion} from "@mui/types";
import Box from '@mui/material/Box';
import { set } from 'date-fns';


// settings data

const headCells: HeadCell[] = [
    {
        id: 'expired',
        numeric: false,
        disablePadding: false,
        label: '',
        width: "5px",
        disableSort: true
    },
    {
        id: 'sourceId',
        numeric: false,
        disablePadding: false,
        label: 'Source',
        width: "100px",
    },
    {
        id: 'tableId',
        numeric: false,
        disablePadding: false,
        label: 'Dataset Name',
        width: "180px",
    },
    {
        id: 'grantee',
        numeric: false,
        disablePadding: false,
        label: 'Grantee',
        width: "160px",
    },
    {
        id: 'treatmentLevel',
        numeric: false,
        disablePadding: false,
        label: 'Treatment',
        width: "5px",
    },
    {
        id: 'status',
        numeric: false,
        disablePadding: false,
        label: 'Request Status',
        width: "40px",
    },
    {
        id: 'createdOn',
        numeric: false,
        disablePadding: false,
        label: 'Requested On Date',
        width: "20px",
    },
    {
        id: 'businessReason',
        numeric: false,
        disablePadding: false,
        label: '',
        width: "5px",
    }
];

const myOverrideRequestsHeadCells: HeadCell[] = [
    {
        id: 'expired',
        numeric: false,
        disablePadding: false,
        label: '',
        width: "5px",
        disableSort: true
    },
    {
        id: 'sourceId',
        numeric: false,
        disablePadding: false,
        label: 'Source',
        width: "150px",
    },
    {
        id: 'tableId',
        numeric: false,
        disablePadding: false,
        label: 'Dataset Name',
        width: "300px",
    },
    {
        id: 'treatmentLevel',
        numeric: false,
        disablePadding: false,
        label: 'Treatment',
        width: "5px",
    },
    {
        id: 'status',
        numeric: false,
        disablePadding: false,
        label: 'Request Status',
        width: "160px",
    },
    {
        id: 'createdOn',
        numeric: false,
        disablePadding: false,
        label: 'Requested On Date',
        width: "100px",
    },
    {
        id: 'businessReason',
        numeric: false,
        disablePadding: false,
        label: '',
        width: "20px",
    }
];

const filterCells: string[] = [
    'sourceId', 'tableId', 'grantee', 'treatmentLevel', 'dataSteward', 'status'
]

const dataStewardFilterCells: string[] = filterCells.filter(item => item !== 'dataSteward');

const myApprovalsFilterCells: string[] = dataStewardFilterCells.filter(item => item !== 'status');

const myOverrideRequestsFilterCells: string[] = filterCells.filter(item => item !== 'grantee');

class OverrideRequestExplorerPrams {
    myOverrideRequests?: boolean = false;
    myApprovals?: boolean = false;
    myActions?: boolean = false;
    tableId?: string = '';
    grantee?: string = '';
    dataSteward?: string = '';
    isAdmin?: boolean;
}

const OverrideRequestsExplorer = (params: OverrideRequestExplorerPrams) => {


    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [dataRows, setDataRows] = useState<IOverrideRequestDetails[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [order, setOrder] = useState<Order>("asc");
    const [orderBy, setOrderBy] = useState<keyof IOverrideRequestDetails>("tableId");
    const [selected, setSelected] = useState<readonly string[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [rowID, setRowID] = useState('');
    const [rowGrantee, setRowGrantee] = useState('');
    const [rowTableId, setRowTableId] = useState('');
    const [rowTreatmentLevel, setRowTreatmentLevel] = useState('');
    const [rowStatusDetailMsg, setRowStatusDetailMsg] = useState('');
    const [rowDataSteward, setRowDataSteward] = useState('');
    const [rowStatus, setRowStatus] = useState('');
    const [deletedIds, setDeletedIds] = useState<string[]>([]);
    const [updatedRequestIds, setUpdatedRequestIds] = useState<string[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [newRowStatus, setNewRowStatus] = useState('');
    const [expiryDate, setExpiryDate] = useState("");
    const [rowActionType, setRowActionType] = useState("");
    const patchOverride = useSelector(patchOverrideRequestSelector);
    const patchPendingOverride = useSelector(patchOverrideRequestPendingSelector);
    const userProfile = useSelector(getProfileDataSelector);
    let grantee = params.grantee ? params.grantee.toLowerCase() : '';
    if (params.myOverrideRequests) {
        grantee = userProfile.userPrincipalName.toLowerCase();
    }
    let dataSteward = '';

    // Filters-related
    const [tableIds, setTableIds] = useState<string[]>(params.tableId ? [params.tableId] : []);
    const [sourceIds, setSourceIds] = useState<string[]>([]);
    const [treatmentLevels, setTreatmentLevels] = useState<string[]>([]);
    const [grantees, setGrantees] = useState<string[]>(grantee ? [grantee] : []);
    const [dataStewards, setDataStewards] = useState<string[]>(dataSteward ? [dataSteward] : []);
    const [statuses, setStatuses] = useState<string[]>([]);
    const [filters, setFilters] = useState<string[]>([]);
    const [filtersResolved, setFiltersResolved] = useState(params.myApprovals ? false : true);
    const [openFilters, setOpenFilters] = useState(false);
    const [deletedIdLoading, setDeletedIdLoading] = useState('')
    const selectable: string = "no";
    const allSelectable: boolean = false;
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // source list    
    const overrideRequestList = useSelector(getOverrideRequestListSelector);
    const loadingOverrideRequestList = useSelector(getOverrideRequestListPendingSelector);

    const [searchParams, setSearchParams] = useState<IOverrideRequestSearchParam>({
        skip: page * rowsPerPage,
        top: rowsPerPage,
        count: true,
        search: searchTerm,
        tableId: tableIds,
        sourceId: sourceIds,
        treatmentLevel: treatmentLevels,
        grantee: grantees,
        orderby: `${orderBy} ${order}`,
        dataSteward: dataStewards,
        status: statuses
    });

    const setMyApprovalsFilters = () => {
        if (params.myApprovals && userProfile) {
            dataSteward = userProfile.userPrincipalName.toLowerCase();
            setDataStewards([dataSteward]);
        }
        if (params.myActions) {
            setStatuses([REQUEST_STATUS_VALUES.awaiting_approval]);
        }
    }

    useEffect(() => {
        if (userProfile) {
            setMyApprovalsFilters();
            setFiltersResolved(true);
        }
    }, [userProfile])

    useEffect(() => {
        window.scrollTo(0, 0)
        if (filtersResolved) {
            setSearchParams({
                skip: page * rowsPerPage,
                top: rowsPerPage,
                count: true,
                search: searchTerm,
                tableId: tableIds,
                sourceId: sourceIds,
                treatmentLevel: treatmentLevels,
                grantee: grantees,
                orderby: `${orderBy} ${order}`,
                dataSteward: dataStewards,
                status: statuses
            });
        } 
    }, [page, rowsPerPage, searchTerm, orderBy, order, grantees, tableIds, sourceIds, treatmentLevels, dataStewards, statuses, filtersResolved]);
    
    
    useEffect(() => {
        if (filtersResolved) {
            dispatch(fetchOverrideRequestsRequest(searchParams));
        }
    }, [dispatch, searchParams]);


    useEffect(() => {
        let data: IOverrideRequestDetails[] = (overrideRequestList.override_requests);
        if (data && data.length > 0) {
            const newData = data.map((item: any, index: number) => {
                return {
                    ...item,
                    status: getRequestStatusText(item.status),
                    error: getErrorMsg(item),
                    expired: item.expiryDate ? expiredStatus(item.expiryDate) : "new",
                    createdOn: convertUTCDateToLocalDate(item.createdOn).toDateString()
                };
            })
            setDataRows(newData);
        }
        else {
            setDataRows(data);
        }
        setTotalRows(overrideRequestList.count);
    }, [overrideRequestList]);

    function getDate(date: any) {
        const result = new Date(date);
        return result
    }

    function expiredStatus(expiryDate: any): OverridableStringUnion<'error' | 'warning' | 'success'> {
        let currentDate = new Date();
        let expiry = new Date(expiryDate);
        let nowPlus30Days = new Date();
        nowPlus30Days.setDate(nowPlus30Days.getDate() + 30);
        if (expiry < currentDate) {
            return "error";
        } else if (expiry > currentDate && expiry < nowPlus30Days) {
            return "warning";
        } else {
            return "success";
        }
      }

    // function getRequestStatusText(value: string) {
    function getErrorMsg(item: IOverrideRequestDetails)  {
        if (item.status === REQUEST_STATUS_VALUES.pending_completion || item.status === REQUEST_STATUS_VALUES.pending_notification) {
            return `Automation Error: ${item.statusDetailMessage}`
        } else {
            return ""
        }
      }
  

    // table related events 

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property as keyof IOverrideRequestDetails);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = dataRows!.map((n: { tableId: any; }) => n.tableId);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const deleteAllClick = () => {
        setSelected([]);
    }

    const filterResults = (event: React.MouseEvent<HTMLButtonElement>, filters: string[]) => {

        let newTables: string[] = params.tableId ? [params.tableId] : [];
        let newSources: string[] = [];
        let newTreatmentLevels: string[] = [];
        let newGrantees: string[] = grantee ? [grantee] : [];
        let newDataStewards: string[] = dataSteward ? [dataSteward] : [];
        let newStatuses: string[] = [];

        filters.forEach(item => {
            let filter: string[] = item.split(": ");
            switch (filter[0]) {
                case 'tableId':
                    newTables.push(filter[1]);
                    break;
                case 'sourceId':
                    newSources.push(filter[1]);
                    break;
                case 'treatmentLevel':
                    newTreatmentLevels.push(filter[1]);
                    break;
                case 'grantee':
                    newGrantees.push(filter[1].toLowerCase());
                    break;
                case 'dataSteward':
                    newDataStewards.push(filter[1].toLowerCase());
                    break;
                case 'status':
                    newStatuses.push(filter[1]);
                    break;
                default:
                    break;
            }
        });

        setTableIds(newTables);
        setSourceIds(newSources);
        setTreatmentLevels(newTreatmentLevels);
        setGrantees(newGrantees);
        setDataStewards(newDataStewards);
        setStatuses(newStatuses);
        setFilters(filters);
        setOpenFilters(true);
        setPage(0);
    }

    const refreshPage = () => {
        setDataRows([]);
        setRowID('');
        setRowGrantee('');
        setRowTableId('');
        setRowTreatmentLevel('');
        setRowStatus('');
        setRowStatusDetailMsg('');
        setRowDataSteward('');
        setExpiryDate('');
        setTableIds(params.tableId ? [params.tableId] : []);
        setSourceIds([]);
        setStatuses([]);
        setTreatmentLevels([]);
        setGrantees(grantee ? [grantee] : []);
        setMyApprovalsFilters();
        setFilters([]);
        setOpenFilters(false);
        setUpdatedRequestIds([]);
        
    }

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPage(0);
        setSearchTerm(e.target.value);
    }

    const handleSearchDebounce = useMemo(() => {
        return debounce(handleSearch, SEARCH_DEBOUNCE_TIME_MILLIS);

    }, []);

    const searchEl = () => {
        return (
            <TextField
                id="override-request-search"
                label="Search Override Requests"
                type="search"
                fullWidth={true}
                onChange={handleSearchDebounce}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
                }}
            />
        );
    };

    const handleOverrideView = (row: IOverrideRequestDetails, event: React.MouseEvent<HTMLButtonElement>) => {
        const url = `/ViewOverRideRequest/${row.requestId}`;
        if (event.ctrlKey) {
            window.open(url, '_blank')
        }
        else {
            navigate(url);
        }
    }

    const confirmOverrideDelete = (row: IOverrideRequestDetails) => {
        setRowActionType('cancel');
        openOverrideRequestDialog(row);
    }

    const openOverrideRequestDialog = (row: IOverrideRequestDetails) => {
        setRowID(row.requestId);
        setRowGrantee(row.grantee);
        setRowTableId(row.tableId);
        setRowTreatmentLevel(row.treatmentLevel);
        setRowStatus(row.status);
        setRowStatusDetailMsg(row.statusDetailMessage);
        setRowDataSteward(row.dataSteward);
        setExpiryDate(row.expiryDate);
        setOpenDialog(true);
    }
    
    const confirmOverrideRequestApproval = (row: IOverrideRequestDetails) => {
        setRowActionType('approve');
        openOverrideRequestDialog(row);

    }

    const confirmOverrideRequestReject = (row: IOverrideRequestDetails) => {
        setRowActionType('reject');
        openOverrideRequestDialog(row);
    }

    const handleCloseDialog = (refreshList?: boolean, requestId?: string) => {
        if (refreshList === true) {
            setUpdatedRequestIds([...updatedRequestIds, requestId!]);
        }
        setOpenDialog(false);
    }

    const confirmOverrideRequestRetryAutomation = (row: IOverrideRequestDetails) => {
        setRowActionType('retry');
        openOverrideRequestDialog(row);

    }

    const confirmOverrideRequestSkipAutomation = (row: IOverrideRequestDetails) => {
        setRowActionType('skip')
        openOverrideRequestDialog(row);;
    }


    const tableToolBar = (props: OverrideRequestExplorerPrams) => {
        if (props.tableId) {
            return <></>
        }
        let filterDropdown = props.myOverrideRequests ? myOverrideRequestsFilterCells : props.isAdmin? filterCells : props.myActions? myApprovalsFilterCells : dataStewardFilterCells;
        return (
            <EnhancedTableToolbar numSelected={selected.length} title={'Override Requests'} headCells={headCells} filterCells={filterDropdown} deleteAll={deleteAllClick}
                filterResults={filterResults} refreshPage={refreshPage} currentFilters={filters} openToolbar={openFilters} explorer={'overrideRequests'}/>
        );
    }

    const tableHeader = (props: OverrideRequestExplorerPrams) => {
        return (
            <EnhancedTableHead
                headCells={props.myOverrideRequests ? myOverrideRequestsHeadCells : headCells}
                numSelected={selected.length}
                actionable={true}
                selectable={allSelectable}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={totalRows}
            />
        );
    }

    const isSelected = (name: string) => selected.indexOf(name) !== -1;

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalRows) : 0;

    const tableRows = (props: OverrideRequestExplorerPrams) => {

        return (
            <>
                {dataRows?.map((row: IOverrideRequestDetails) => {
                        const isItemSelected = isSelected(row.requestId);
                        var isItemError = false;
                        if (row.status === REQUEST_STATUS.pending_completion || row.status === REQUEST_STATUS.pending_notification) {
                            isItemError = true;
                        }
                        const closedStatuses = [REQUEST_STATUS.completed, REQUEST_STATUS.cancelled, REQUEST_STATUS.approved, REQUEST_STATUS.rejected]
                        const isRequestClosed = closedStatuses.includes(row.status);
                        const requestUpdated = updatedRequestIds.includes(row.requestId);
                        return (
                            <Row row={row}
                                key={row.requestId}
                                headCells={props.myOverrideRequests ? myOverrideRequestsHeadCells : headCells}
                                selectable={selectable}
                                canView={true}
                                canDelete={(props.isAdmin || props.myOverrideRequests) && !isRequestClosed}
                                canExpand={false}
                                canApprove={(props.isAdmin || row.status === REQUEST_STATUS.awaiting_approval) && !props.myOverrideRequests}
                                canRetry={props.isAdmin && isItemError && !props.myOverrideRequests}
                                canSkip={props.isAdmin && isItemError && !props.myOverrideRequests}
                                isItemSelected={isItemSelected}
                                handleView={handleOverrideView}
                                handleDelete={confirmOverrideDelete}
                                handleApprove={confirmOverrideRequestApproval}
                                handleReject={confirmOverrideRequestReject}
                                handleRetry={confirmOverrideRequestRetryAutomation}
                                handleSkip={confirmOverrideRequestSkipAutomation}
                                deletedRows={deletedIds}
                                loadingIcon={patchPendingOverride}
                                deletedIdLoading={deletedIdLoading}
                                deleteId={patchOverride}
                                disabledButton={requestUpdated}
                            >
                            </Row>
                        );
                    })}
                {emptyRows > 0 && emptyRows < 10 && (
                    <TableRow
                        style={{
                            height: (53) * emptyRows,
                        }}
                    >
                        <TableCell colSpan={12} />
                    </TableRow>
                )}
                {dataRows?.length < 1 && (
                    <TableRow
                        style={{
                            height: (53) * emptyRows,
                        }}
                    >
                        <TableCell colSpan={6}>There are no override requests available.</TableCell>
                    </TableRow>
                )}
            </>
        );
    }

    const listGrid = (props: OverrideRequestExplorerPrams) => {

        return (
            <>
                {/* adding skeleton screen on loading */}
                {
                    loadingOverrideRequestList ?
                        <Grid container width="100%">
                            <Grid item xs={12}>
                                <Skeleton animation="wave" height={60} />
                            </Grid>
                            <Grid item xs={12}>
                                <Skeleton animation="wave" height={60} />
                            </Grid>
                            <Grid item xs={12}>
                                <Skeleton animation="wave" height={60} />
                            </Grid>
                            <Grid item xs={12}>
                                <Skeleton animation="wave" height={60} />
                            </Grid>
                            <Grid item xs={12}>
                                <Skeleton animation="wave" height={60} />
                            </Grid>
                            <Grid item xs={12}>
                                <Skeleton animation="wave" height={60} />
                            </Grid>
                            <Grid item xs={12}>
                                <Skeleton animation="wave" height={60} />
                            </Grid>
                            <Grid item xs={12}>
                                <Skeleton animation="wave" height={60} />
                            </Grid>
                        </Grid>

                        :
                        <EnhancedTable
                            totalRows={totalRows}
                            tableToolBar={tableToolBar(props)}
                            tableHeader={tableHeader(props)}
                            tableRows={tableRows(props)}
                            pagination={true}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            handleChangePage={handleChangePage}
                            handleChangeRowsPerPage={handleChangeRowsPerPage}
                        />
                }
                <OverrideRequestUpdateStatus 
                    isOpen={openDialog}
                    handleClose={handleCloseDialog}
                    tableId={rowTableId} 
                    grantee={rowGrantee}
                    treatmentLevel={rowTreatmentLevel}
                    requestId={rowID}
                    requestStatus={rowStatus}
                    statusDetailMessage={rowStatusDetailMsg}
                    expiryDate={expiryDate}
                    dataSteward={rowDataSteward}
                    updateActionType={rowActionType}
                />
            </>
        );
    }


    if (params.myOverrideRequests) {
        return (
            <>
                <SearchListGrid searchEl={searchEl()}  hideSearch={!!params.tableId} children={
                    <Box sx={{ padding: "10px 0", }}>
                        {listGrid(params)}
                    </Box>
                }/>
            </>
        )
    } else {
        return (
            <>
                <SearchListGrid searchEl={searchEl()} hideSearch={!!params.tableId} children={listGrid(params)}/>
                    
                </>
        );
    }
}

export default OverrideRequestsExplorer;